import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Routes } from "constants/routes";
import { useLabel } from '../hooks/useLabel';
import { useLabels } from '../hooks/useLabels';
import { DeleteLabelModal } from "../DeleteLabelModal";
import { Overview } from "./Overview";
import { Form } from "./Form";
import { Releases } from "components/Labels/LabelDetail/Releases";
import { useCountries } from "components/Labels/hooks/useCountries";
import { PaginatorPageChangeEvent } from "primereact/paginator";
import IntlStore from "stories/IntlStore";

export const LabelDetail = () => {
  const { id } = useParams();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1000);
  const { label, updating, updateLabel } = useLabel(id ?? '');
  const { deleting, deleteLabel } = useLabels(currentPage, pageCount);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const toast = useRef<any>(null);
  const navigate = useNavigate();
  const { countries } = useCountries();
  const [first, setFirst] = useState<number>(0);

  if (!label || !id) {
    return (
      <Loader>
        <ProgressSpinner />
      </Loader>
    );
  }

  const onDeleteLabel = async () => {
    try {
      if (!deleteId) return;
      await deleteLabel(deleteId);
      setDeleteId(null);
      navigate(Routes.Labels);
      toast.current?.show({
        severity: 'success',
        summary: IntlStore.intl.formatMessage({
          id: "Labels.Success",
          defaultMessage: "Success"
        }),
        detail: IntlStore.intl.formatMessage({
          id: "Labels.LabelRemovedSuccessfully",
          defaultMessage: "Label removed successfully"
        })
      });
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: IntlStore.intl.formatMessage({
          id: "Labels.Error",
          defaultMessage: "Error"
        }),
        detail: e.message
      });
    }
  };

  const onDelete = () => setDeleteId(id);

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    setCurrentPage(event.page + 1);
    setFirst(event.rows * event.page);
    setPageCount(event.rows);
  };

  return (
    <Container>
      <Form
        id={label.id}
        name={label.name ?? ''}
        description={label.description ?? ''}
        releasesCount={label.releases?.length}
        src={label.cover_url}
        onDelete={onDelete}
        onSave={updateLabel}
        updating={updating}
      />
      <Overview
        updating={updating}
        onSave={updateLabel}
        countries={countries}
        country={label.country}
        genres={label.genres?.map(genre => genre.name).join(', ') || '-'}
        year={label.year}
        artists={label.artists?.map(artist => artist.name).join(', ') || '-'}
      />
      <DeleteLabelModal
        deleting={deleting}
        onDeleteLabel={onDeleteLabel}
        onHide={() => setDeleteId(null)}
        isOpen={!!deleteId}
      />
      <Releases count={label.releases?.length || 0} releases={label.releases} currentPage={currentPage} pageCount={pageCount} onPageChange={onPageChange} />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  background-color: ${props => props.theme.palette.background.base};
  height: 100%;
`;

const Loader = styled.div`
  height: 100%;
  width: 100%;
  background-color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 30px;
`;
